import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, ShieldCheck, MapPin, Package, ShoppingBag, BarChart3, Pencil, Trash2, Link2, Phone, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = { pending: 'bg-yellow-100 text-yellow-800', preparing: 'bg-blue-100 text-blue-800', packed: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800' };

export default function AdminFarmerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [farmer, setFarmer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editLink, setEditLink] = useState(null);
  const [savingLink, setSavingLink] = useState(false);

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [linkSelections, setLinkSelections] = useState({});
  const [savingLinks, setSavingLinks] = useState(false);

  const loadFarmer = () => {
    api.get(`/admin/farmers/${id}`).then(r => { setFarmer(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { loadFarmer(); }, [id]); // eslint-disable-line

  const openLinkDialog = async () => {
    setLinkDialogOpen(true);
    setLinkSelections({});
    if (allProducts.length === 0) {
      setLoadingProducts(true);
      try {
        const r = await api.get('/admin/products');
        setAllProducts(r.data || []);
      } catch { toast.error('Failed to load products'); }
      finally { setLoadingProducts(false); }
    }
  };

  const toggleSelection = (productId) => {
    setLinkSelections(prev => {
      const next = { ...prev };
      if (next[productId]?.selected) {
        next[productId] = { ...next[productId], selected: false };
      } else {
        next[productId] = {
          selected: true,
          procurement_price: next[productId]?.procurement_price || '',
          expected_harvest_date: next[productId]?.expected_harvest_date || '',
          approximate_stock_kg: next[productId]?.approximate_stock_kg || '',
        };
      }
      return next;
    });
  };

  const updateSelectionField = (productId, field, value) => {
    setLinkSelections(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || { selected: true }), [field]: value },
    }));
  };

  const saveNewLinks = async () => {
    const selected = Object.entries(linkSelections).filter(([, v]) => v.selected);
    if (selected.length === 0) { toast.error('Select at least one product'); return; }
    setSavingLinks(true);
    try {
      for (const [productId, v] of selected) {
        await api.post('/admin/farmer-products', {
          farmer_id: id, product_id: productId,
          procurement_price: parseFloat(v.procurement_price) || 0,
          expected_harvest_date: v.expected_harvest_date || '',
          approximate_stock_kg: parseFloat(v.approximate_stock_kg) || 0,
          is_active: true,
        });
      }
      toast.success(`${selected.length} product(s) linked`);
      setLinkDialogOpen(false);
      setLinkSelections({});
      loadFarmer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    } finally { setSavingLinks(false); }
  };

  const toggleStatus = async () => {
    const newStatus = farmer.status === 'published' ? 'draft' : 'published';
    try {
      await api.put(`/admin/farmers/${id}`, { status: newStatus });
      setFarmer({ ...farmer, status: newStatus });
      toast.success(`Farmer ${newStatus}`);
    } catch { toast.error('Failed to update'); }
  };

  const unlinkProduct = async (p) => {
    if (!p.link_id) { toast.error('Not linked via junction table'); return; }
    if (!window.confirm(`Unlink "${p.name}" from ${farmer.name}? The product will remain in the system, just no longer sourced from this farmer.`)) return;
    try {
      await api.delete(`/admin/farmer-products/${p.link_id}`);
      toast.success('Product unlinked');
      loadFarmer();
    } catch { toast.error('Failed to unlink'); }
  };

  const saveLinkEdit = async () => {
    if (!editLink?.link_id) return;
    setSavingLink(true);
    try {
      await api.put(`/admin/farmer-products/${editLink.link_id}`, {
        procurement_price: parseFloat(editLink.procurement_price) || 0,
        expected_harvest_date: editLink.link_expected_harvest_date || '',
        approximate_stock_kg: parseFloat(editLink.approximate_stock_kg) || 0,
        is_active: editLink.link_active !== false,
      });
      toast.success('Link updated');
      setEditLink(null);
      loadFarmer();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingLink(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-full p-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!farmer) return <div className="p-8"><p>Farmer not found</p></div>;

  const linkedProductIds = new Set((farmer.products || []).map(p => p.product_id));
  const availableProducts = allProducts.filter(p =>
    !linkedProductIds.has(p.product_id) &&
    (!productSearch || (p.name || '').toLowerCase().includes(productSearch.toLowerCase()))
  );
  const selectedCount = Object.values(linkSelections).filter(v => v.selected).length;

  return (
    <div data-testid="admin-farmer-detail" className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-3">
            <img src={farmer.profile_image} alt="" className="w-12 h-12 rounded-xl object-cover" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-serif font-medium">{farmer.name}</h1>
                {farmer.verification_status === 'verified' && <ShieldCheck className="h-4 w-4 text-primary" />}
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{farmer.farm_name} &middot; {farmer.location}</p>
            </div>
          </div>
        </div>
        <Badge variant={farmer.status === 'published' ? 'default' : 'secondary'} className="rounded-full capitalize">{farmer.status}</Badge>
        <Link to={`/admin/farmers/${id}/edit`}>
          <Button data-testid="edit-farmer-btn" variant="outline" size="sm" className="rounded-full gap-2"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
        </Link>
        <Button data-testid="toggle-farmer-status" variant="outline" size="sm" onClick={toggleStatus} className="rounded-full">
          {farmer.status === 'published' ? 'Unpublish' : 'Publish'}
        </Button>
        {farmer.status !== 'published' && (
          <Button data-testid="delete-farmer-btn" variant="destructive" size="sm" onClick={async () => {
            if (!window.confirm(`Are you sure? This will permanently delete ${farmer.name} and remove all their product links. This cannot be undone.`)) return;
            try {
              await api.delete(`/admin/farmers/${id}`);
              toast.success('Farmer deleted');
              navigate('/admin/farmers');
            } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
          }} className="rounded-full gap-2">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        )}
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="rounded-full mb-6">
          <TabsTrigger value="profile" className="rounded-full">Profile</TabsTrigger>
          <TabsTrigger value="products" className="rounded-full">Products</TabsTrigger>
          <TabsTrigger value="inventory" className="rounded-full">Inventory</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-full">Orders</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-full">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-xl">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-3">Story</h3>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{farmer.story}</p>
                {farmer.intro_video && <video src={farmer.intro_video} controls className="w-full rounded-xl mt-4 max-h-56" />}
              </CardContent>
            </Card>
            <Card className="rounded-xl">
              <CardContent className="pt-6">
                {(farmer.farmer_phone || farmer.farmer_whatsapp || farmer.farmer_email) && (
                  <>
                    <h3 className="font-medium mb-3">Contact (Admin-only)</h3>
                    <div className="space-y-1.5 mb-6 text-sm">
                      {farmer.farmer_phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /> {farmer.farmer_phone}</p>}
                      {farmer.farmer_whatsapp && <p className="flex items-center gap-2"><MessageCircle className="h-3.5 w-3.5 text-muted-foreground" /> {farmer.farmer_whatsapp}</p>}
                      {farmer.farmer_email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /> {farmer.farmer_email}</p>}
                    </div>
                  </>
                )}
                <h3 className="font-medium mb-3">Farming Practices</h3>
                <div className="flex flex-wrap gap-2">
                  {farmer.farming_practices?.map((p, i) => <Badge key={i} variant="secondary" className="rounded-full text-xs">{p}</Badge>)}
                </div>
                <h3 className="font-medium mb-3 mt-6">Lab Reports</h3>
                <div className="space-y-1.5">
                  {farmer.lab_reports?.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="flex-1">{r.name || `Report ${i+1}`}</span>
                      {r.url && r.url !== '#' && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View</a>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="products">
          <Card className="rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h3 className="font-medium">Linked Products ({farmer.products?.length || 0})</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Products are managed in the <Link to="/admin/products" className="text-primary hover:underline">Products module</Link>. Manage which products this farmer supplies here.
                  </p>
                </div>
                <Button data-testid="link-product-btn" variant="default" size="sm" onClick={openLinkDialog} className="rounded-full gap-2">
                  <Link2 className="h-3.5 w-3.5" /> Link Product
                </Button>
              </div>
              {(!farmer.products || farmer.products.length === 0) ? (
                <p className="text-sm text-muted-foreground text-center py-10">No products linked yet. Click "Link Product" to add.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Procurement</TableHead>
                      <TableHead>Approx Stock</TableHead>
                      <TableHead>Harvest Date</TableHead>
                      <TableHead>Link Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {farmer.products.map((p) => (
                      <TableRow key={p.product_id} data-testid={`linked-product-row-${p.product_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {p.images?.[0] && <img src={p.images[0]} alt="" className="w-9 h-9 rounded-lg object-cover" />}
                            <div>
                              <Link to={`/admin/products/${p.product_id}/edit`} className="font-medium text-sm hover:text-primary hover:underline">{p.name}</Link>
                              <p className="text-xs text-muted-foreground">{'\u20B9'}{p.price_per_kg}/kg retail</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize text-sm">{p.category}</TableCell>
                        <TableCell>{'\u20B9'}{p.procurement_price || 0}/kg</TableCell>
                        <TableCell>{p.approximate_stock_kg || 0} kg</TableCell>
                        <TableCell className="text-sm">{p.link_expected_harvest_date || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={p.link_active ? 'default' : 'secondary'} className="rounded-full text-xs capitalize">
                            {p.link_active ? 'Active' : 'Paused'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button data-testid={`edit-link-${p.product_id}`} variant="ghost" size="icon" title="Edit link details" onClick={() => setEditLink({ ...p })}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button data-testid={`unlink-product-${p.product_id}`} variant="ghost" size="icon" title="Unlink product" onClick={() => unlinkProduct(p)}>
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inventory">
          <Card className="rounded-xl">
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Approx Stock</TableHead>
                    <TableHead>Harvest Date</TableHead>
                    <TableHead>Procurement</TableHead>
                    <TableHead>Retail Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {farmer.products?.map((p) => (
                    <TableRow key={p.product_id}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.approximate_stock_kg || 0} kg</TableCell>
                      <TableCell>{p.link_expected_harvest_date || '-'}</TableCell>
                      <TableCell>{'\u20B9'}{p.procurement_price || 0}/kg</TableCell>
                      <TableCell>{'\u20B9'}{p.price_per_kg}/kg</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orders">
          <Card className="rounded-xl">
            <CardContent className="pt-6">
              {(!farmer.orders || farmer.orders.length === 0) ? <p className="text-sm text-muted-foreground text-center py-8">No orders yet</p> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Assigned KG</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {farmer.orders.map((o) => (
                      <TableRow key={o.order_id}>
                        <TableCell className="font-medium">{o.order_id}</TableCell>
                        <TableCell>{o.customer_name}</TableCell>
                        <TableCell>{o.assigned_kg || '-'} kg</TableCell>
                        <TableCell><Badge className={`${statusColors[o.status]} border-0 rounded-full text-xs capitalize`}>{o.status}</Badge></TableCell>
                        <TableCell>{new Date(o.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="rounded-xl"><CardContent className="pt-6 text-center"><ShoppingBag className="h-8 w-8 mx-auto text-blue-500 mb-2" /><p className="text-2xl font-semibold">{farmer.analytics?.total_orders || 0}</p><p className="text-xs text-muted-foreground">Total Orders</p></CardContent></Card>
            <Card className="rounded-xl"><CardContent className="pt-6 text-center"><BarChart3 className="h-8 w-8 mx-auto text-green-500 mb-2" /><p className="text-2xl font-semibold">{'\u20B9'}{farmer.analytics?.total_sales?.toLocaleString() || 0}</p><p className="text-xs text-muted-foreground">Total Sales</p></CardContent></Card>
            <Card className="rounded-xl"><CardContent className="pt-6 text-center"><Package className="h-8 w-8 mx-auto text-purple-500 mb-2" /><p className="text-2xl font-semibold">{farmer.analytics?.product_count || 0}</p><p className="text-xs text-muted-foreground">Products</p></CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editLink} onOpenChange={(o) => !o && setEditLink(null)}>
        <DialogContent className="max-w-md" data-testid="edit-link-dialog">
          <DialogHeader><DialogTitle>Edit Link: {editLink?.name}</DialogTitle></DialogHeader>
          {editLink && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                These fields describe how <strong>{farmer.name}</strong> supplies this product. Product details (name, price, images) are edited in the <Link to={`/admin/products/${editLink.product_id}/edit`} className="text-primary hover:underline">Products module</Link>.
              </p>
              <div><Label>Procurement Price ({'\u20B9'}/kg)</Label><Input data-testid="edit-link-procurement" type="number" step="0.01" value={editLink.procurement_price || ''} onChange={e => setEditLink({...editLink, procurement_price: e.target.value})} className="mt-1" /></div>
              <div><Label>Expected Harvest Date</Label><Input data-testid="edit-link-harvest" type="date" value={editLink.link_expected_harvest_date || ''} onChange={e => setEditLink({...editLink, link_expected_harvest_date: e.target.value})} className="mt-1" /></div>
              <div><Label>Approximate Stock (kg)</Label><Input data-testid="edit-link-stock" type="number" value={editLink.approximate_stock_kg || ''} onChange={e => setEditLink({...editLink, approximate_stock_kg: e.target.value})} className="mt-1" /></div>
              <div className="flex items-center gap-2">
                <Checkbox checked={editLink.link_active !== false} onCheckedChange={v => setEditLink({...editLink, link_active: v})} />
                <Label className="cursor-pointer" onClick={() => setEditLink({...editLink, link_active: !(editLink.link_active !== false)})}>Link is active (farmer currently supplies this product)</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLink(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-link-edit-btn" onClick={saveLinkEdit} className="rounded-full" disabled={savingLink}>{savingLink ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="link-product-dialog">
          <DialogHeader>
            <DialogTitle>Link Products to {farmer.name}</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">Select products from the Products module and specify procurement details.</p>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="link-dialog-search" placeholder="Search products..." value={productSearch} onChange={e => setProductSearch(e.target.value)} />
            {loadingProducts ? (
              <p className="text-center py-10 text-sm text-muted-foreground">Loading...</p>
            ) : availableProducts.length === 0 ? (
              <div className="text-center py-10 space-y-2">
                <Package className="h-10 w-10 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">
                  {allProducts.length === 0 ? 'No products in the system yet.' : 'All available products are already linked.'}
                </p>
                <Button variant="outline" size="sm" onClick={() => window.open('/admin/products/new', '_blank')} className="rounded-full mt-2">Create new product</Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {availableProducts.map(p => {
                  const l = linkSelections[p.product_id];
                  const isSelected = !!l?.selected;
                  return (
                    <div key={p.product_id}
                      className={`rounded-xl border p-3 ${isSelected ? 'border-primary bg-primary/5' : ''}`}>
                      <div className="flex items-center gap-3">
                        <Checkbox data-testid={`dialog-link-checkbox-${p.product_id}`} checked={isSelected} onCheckedChange={() => toggleSelection(p.product_id)} />
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{p.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{p.category} · {'\u20B9'}{p.price_per_kg}/kg</p>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 pl-7">
                          <div>
                            <Label className="text-xs">Procurement ({'\u20B9'}/kg)</Label>
                            <Input data-testid={`dialog-procurement-${p.product_id}`} type="number" step="0.01" className="mt-1 h-8 text-sm"
                              value={l.procurement_price} onChange={e => updateSelectionField(p.product_id, 'procurement_price', e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Harvest Date</Label>
                            <Input type="date" className="mt-1 h-8 text-sm"
                              value={l.expected_harvest_date} onChange={e => updateSelectionField(p.product_id, 'expected_harvest_date', e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Stock (kg)</Label>
                            <Input type="number" className="mt-1 h-8 text-sm"
                              value={l.approximate_stock_kg} onChange={e => updateSelectionField(p.product_id, 'approximate_stock_kg', e.target.value)} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-new-links-btn" onClick={saveNewLinks} className="rounded-full" disabled={savingLinks || selectedCount === 0}>
              {savingLinks ? 'Linking...' : `Link ${selectedCount} product(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
